'use client'

import { useEffect, useRef, useState, useCallback } from 'react'

// Klatki 360° leżą na publicznym storage tradeinn jako sekwencja webp:
//   products_360/{prefix}/{id}/{id}-ld-NNNN.webp   (prefix = id bez 4 ostatnich cyfr)
function frameUrl(modelId: string, i: number) {
  // Folder = id bez 4 ostatnich cyfr; krótkie id (≤4 cyfry) leżą w folderze "0"
  // — zgodne z konwencją ścieżek /f/{prefix}/{id}/ tradeinn.
  const prefix = modelId.length > 4 ? modelId.slice(0, -4) : '0'
  const n = String(i).padStart(4, '0')
  return `https://storage.googleapis.com/tradeinn-images/web/products_360/${prefix}/${modelId}/${modelId}-ld-${n}.webp`
}

interface Spin360ViewerProps {
  modelId: string
  frames: number
  alt: string
}

export function Spin360Viewer({ modelId, frames, alt }: Spin360ViewerProps) {
  const [frame, setFrame] = useState(0)
  const [loaded, setLoaded] = useState(0)
  const [dragging, setDragging] = useState(false)
  const [hintVisible, setHintVisible] = useState(true)
  const startX = useRef(0)
  const startFrame = useRef(0)

  // Preload wszystkich klatek — bez tego obrót „miga" przy pierwszym przejściu.
  useEffect(() => {
    let active = true
    let done = 0
    for (let i = 0; i < frames; i++) {
      const img = new Image()
      img.onload = img.onerror = () => {
        if (!active) return
        done++
        setLoaded(done)
      }
      img.src = frameUrl(modelId, i)
    }
    return () => { active = false }
  }, [modelId, frames])

  const rotate = useCallback((clientX: number) => {
    const dx = clientX - startX.current
    // Pełny obrót na ~szerokość 1.5× kontenera; ujemne dx = obrót w drugą stronę.
    const step = Math.round(dx / 6)
    const next = (((startFrame.current - step) % frames) + frames) % frames
    setFrame(next)
  }, [frames])

  function onPointerDown(e: React.PointerEvent) {
    setDragging(true)
    setHintVisible(false)
    startX.current = e.clientX
    startFrame.current = frame
    ;(e.target as HTMLElement).setPointerCapture(e.pointerId)
  }
  function onPointerMove(e: React.PointerEvent) {
    if (!dragging) return
    rotate(e.clientX)
  }
  function onPointerUp(e: React.PointerEvent) {
    setDragging(false)
    ;(e.target as HTMLElement).releasePointerCapture?.(e.pointerId)
  }

  const ready = loaded >= frames
  const pct = Math.round((loaded / frames) * 100)

  return (
    <div
      className="relative w-full h-full select-none"
      style={{ cursor: dragging ? 'grabbing' : 'grab', touchAction: 'pan-y' }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      role="img"
      aria-label={`${alt} — widok 360 stopni, przeciągnij aby obrócić`}
    >
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src={frameUrl(modelId, frame)}
        alt={alt}
        draggable={false}
        className="w-full h-full object-cover pointer-events-none"
      />

      {/* Loader dopóki klatki się ładują */}
      {!ready && (
        <div
          className="absolute inset-0 flex flex-col items-center justify-center gap-2"
          style={{ backgroundColor: 'rgba(13,30,48,0.6)', color: 'rgba(255,255,255,0.8)' }}
        >
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="animate-spin" aria-hidden="true">
            <path d="M21 12a9 9 0 1 1-6.219-8.56" />
          </svg>
          <span className="text-xs font-medium">Ładowanie 360°… {pct}%</span>
        </div>
      )}

      {/* Wskazówka „przeciągnij" */}
      {ready && hintVisible && (
        <div
          className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5 px-3 py-1.5 rounded-full pointer-events-none"
          style={{ backgroundColor: 'rgba(3,8,15,0.7)', color: 'rgba(255,255,255,0.85)' }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" /><path d="M3 3v5h5" />
          </svg>
          <span className="text-xs font-medium">Przeciągnij, aby obrócić</span>
        </div>
      )}
    </div>
  )
}
